import {
  EAiActionStatus,
  EDatasourceType,
  ESdChangeReason,
  IActionDefinitionDto,
  IDatasourceDto,
  IVisionTemplate,
  IVisionWorkflowData,
  parseVisionInfo,
  parseVisionTemplate
} from "@kuika/kuika-cl-model";
import ObjectHash from "object-hash";
import { connect } from "react-redux";
import { ReactFlowProvider } from "reactflow";
import { DatasourceService } from "../../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { AppState } from "../../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../../store/unsaved-changes-modal/run-parked-navigation";
import CloseConfirmationModal from "../../../common/close-confirmation-modal";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableStatusCell,
  KuikaListTableUserCell
} from "../../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTemplateCard, {
  KuikaTemplateCardGrid,
  KuikaTemplateCardSkeleton
} from "../../../ui-kit/kuika-template-card/kuika-template-card";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import CreateScratchEmptyState from "../common/create-scratch-empty-state/create-scratch-empty-state";
import "../ai/ai-action.scss";
import { requestDatasourceReveal } from "../datasource-reveal";
import "./vision-action.scss";
import { AiActionSwitcherChip } from "../editor-switcher/ai-action-switcher-chip";
import VisionTemplateDetailModal from "./vision-template-detail-modal";
import { DnDProvider } from "./workflow-builder/dnd-context";
import { WorkflowProvider } from "./workflow-builder/workflow-context";
import WorkflowBuilder from "./workflow-builder/WorkflowBuilder";

/**
 * Extended template interface with parsed workflow data for frontend use
 */
interface IVisionActionTemplate extends IVisionTemplate {
  workflowData: IVisionWorkflowData;
  // Type is extracted from workflowData.type for easier filtering
  type?: string;
}

interface ICreateVisionActionProps {
  actionDatasource: IDatasourceDto;
  actionToEdit?: IActionDefinitionDto;
  showModal?: boolean;
  nextCallback?: () => void;
  setShowModal?: (show: boolean) => void;
  setNextCallbackFunction?: (cb?: () => void) => void;
}

interface ICreateVisionActionState {
  // Selection state
  selectedTemplate?: IVisionActionTemplate;
  createFromScratch: boolean;

  // Templates tab state
  templates: IVisionActionTemplate[];
  templateSearchTerm: string;
  selectedTypes: string[]; // EVisionTemplateTypes filter
  selectedTags: string[];
  templateSortBy: "recent" | "popular" | "topRated";

  // Editor state
  showEditor: boolean;
  actionName: string;

  // Loading states
  isLoadingTemplates: boolean;

  // Close confirmation modal
  showCloseModal: boolean;

  // Current workflow state for change detection
  currentNodes: any[];
  currentEdges: any[];
  currentWorkflowName: string;

  // The action currently loaded in the editor — this is the source of truth,
  // NOT this.props.actionToEdit (which changes immediately on parent interaction).
  effectiveActionToEdit?: IActionDefinitionDto;

  // True while the unsaved-changes modal is open during an action transition.
  // Prevents componentDidUpdate from re-triggering and keeps the editor stable.
  isTransitionPending: boolean;

  // Template detail modal state
  modalTemplate?: IVisionActionTemplate;
  isModalVisible: boolean;
  isCreatingFromTemplate: boolean;
  activeTab: "templates" | "recents";
  recentsMenuId: string | null;
  recentsMenuPosition: { top: number; left: number } | null;
  actionToDelete?: IActionDefinitionDto;
  isDeleteActionModalVisible: boolean;
  isScratchSidebarVisible: boolean;
  isScratchEmptyStateVisible: boolean;
  /** Where the workflow's save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}

/** The tabs in their on-screen order, so a switch slides towards the one chosen. */
const VISION_TAB_ORDER: string[] = ["templates", "recents"];

class CreateVisionAction extends PureSdComponent<ICreateVisionActionProps & IWithSDProps, ICreateVisionActionState> {
  // Hash for tracking changes
  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  scratchFeatures: string[] = [
    "Build multi-step vision workflows",
    "Connect cameras, models, and data sources",
    "Define detection logic and output behavior",
    "Coordinate vision outputs with downstream AI actions"
  ];

  constructor(props: ICreateVisionActionProps & IWithSDProps) {
    super(props);
    // If editing an existing action, go directly to editor
    const isEditing = !!props.actionToEdit;
    // For new actions we want the Vision_Action1-style generator. `activeCreatingActionName`
    // may hold a display string like "Vision Action" pushed by AI Hub — never use it as the
    // actual action name. It's only used as a fallback label when editing an unsaved row.
    const initialName = isEditing
      ? props.actionToEdit?.name || props.sd.activeCreatingActionName || ""
      : CreateVisionAction.computeDefaultVisionActionName(props.sd?.actionDefinitions);

    // Parse initial nodes/edges if editing
    let initialNodes: any[] = [];
    let initialEdges: any[] = [];
    if (isEditing && props.actionToEdit?.visionInfo) {
      try {
        const parsed = parseVisionInfo(props.actionToEdit.visionInfo);
        initialNodes = parsed.nodes || [];
        initialEdges = parsed.edges || [];
      } catch (e) {
        console.error("Failed to parse visionInfo:", e);
      }
    }

    this.state = {
      createFromScratch: isEditing,
      templates: [],
      templateSearchTerm: "",
      selectedTypes: [],
      selectedTags: [],
      templateSortBy: "popular",
      showEditor: isEditing, // Go directly to editor if editing
      actionName: initialName,
      isLoadingTemplates: !isEditing, // Don't load templates if editing
      showCloseModal: false,
      currentNodes: initialNodes,
      currentEdges: initialEdges,
      currentWorkflowName: initialName,
      effectiveActionToEdit: props.actionToEdit,
      isTransitionPending: false,
      isModalVisible: false,
      modalTemplate: undefined,
      isCreatingFromTemplate: false,
      activeTab: "templates",
      recentsMenuId: null,
      recentsMenuPosition: null,
      actionToDelete: undefined,
      isDeleteActionModalVisible: false,
      isScratchSidebarVisible: false,
      isScratchEmptyStateVisible: false,
      saveStatus: "idle"
    };
  }

  toggleRecentsMenu = (event: React.MouseEvent<HTMLButtonElement>, menuId: string) => {
    event.stopPropagation();

    if (this.state.recentsMenuId === menuId) {
      this.setState({ recentsMenuId: null, recentsMenuPosition: null });
      return;
    }

    const triggerRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const menuWidth = 100;
    const menuItemHeight = 32;
    const menuVerticalPadding = 8;
    const menuHeight = 2 * menuItemHeight + menuVerticalPadding;
    const offset = 4;
    const viewportPadding = 8;

    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    let top = triggerRect.bottom + offset;
    if (spaceBelow < menuHeight + offset && spaceAbove >= menuHeight + offset) {
      top = triggerRect.top - menuHeight - offset;
    } else if (spaceBelow < menuHeight + offset && spaceAbove < menuHeight + offset) {
      top = Math.max(viewportPadding, viewportHeight - menuHeight - viewportPadding);
    }

    const left = Math.max(
      viewportPadding,
      Math.min(triggerRect.right - menuWidth, viewportWidth - menuWidth - viewportPadding)
    );

    this.setState({
      recentsMenuId: menuId,
      recentsMenuPosition: { top, left }
    });
  };

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  async componentDidMount() {
    // Only load templates if not editing an existing action
    if (!this.props.actionToEdit) {
      await this.loadTemplates();
    }
    // Set initial hash
    this.objectHash = this.getObjectHash();
  }

  componentDidUpdate(prevProps: ICreateVisionActionProps & IWithSDProps) {
    // Check if navigation callback is set (user tried to navigate away)
    if (this.props.nextCallback && !prevProps.nextCallback) {
      this.handleCloseDatasource();
      return;
    }

    // Don't react to prop changes while a transition modal is already open
    if (this.state.isTransitionPending) {
      return;
    }

    // Detect external prop changes (user clicked edit/create from datasource panel)
    const actionChanged =
      this.props.actionDatasource?.id !== prevProps.actionDatasource?.id ||
      this.props.actionToEdit?.id !== prevProps.actionToEdit?.id;

    if (actionChanged) {
      if (this.hasUnsavedChanges()) {
        // Freeze the editor in its current state and show the confirmation modal.
        // effectiveActionToEdit stays unchanged → key stays the same → no remount.
        this.setState({ isTransitionPending: true, showCloseModal: true });
      } else {
        // No unsaved changes — transition immediately
        this.applyTransition();
      }
    }
  }

  /**
   * Apply the transition to the new action (from current props).
   * Resets the entire editor state to match the incoming props.
   */
  applyTransition = async () => {
    const isEditing = !!this.props.actionToEdit;
    const initialName = isEditing
      ? this.props.actionToEdit?.name || this.props.sd.activeCreatingActionName || ""
      : this.generateDefaultVisionActionName();

    // Parse initial nodes/edges if editing
    let initialNodes: any[] = [];
    let initialEdges: any[] = [];
    if (isEditing && this.props.actionToEdit?.visionInfo) {
      try {
        const parsed = parseVisionInfo(this.props.actionToEdit.visionInfo);
        initialNodes = parsed.nodes || [];
        initialEdges = parsed.edges || [];
      } catch (e) {
        console.error("Failed to parse visionInfo:", e);
      }
    }

    this.setState(
      {
        createFromScratch: isEditing,
        selectedTemplate: undefined,
        templateSearchTerm: "",
        selectedTypes: [],
        selectedTags: [],
        templateSortBy: "popular",
        showEditor: isEditing, // Go directly to editor if editing
        actionName: initialName,
        isLoadingTemplates: !isEditing,
        showCloseModal: false,
        currentNodes: initialNodes,
        currentEdges: initialEdges,
        currentWorkflowName: initialName,
        effectiveActionToEdit: this.props.actionToEdit, // Sync source of truth
        isTransitionPending: false,
        isScratchEmptyStateVisible: false
      },
      async () => {
        if (!isEditing) {
          await this.loadTemplates();
        }
        this.objectHash = this.getObjectHash();
      }
    );
  };

  getObjectHash(): string {
    const { actionName, currentNodes, currentEdges, currentWorkflowName } = this.state;
    return ObjectHash({
      actionName,
      currentNodes,
      currentEdges,
      currentWorkflowName
    });
  }

  hasUnsavedChanges(): boolean {
    return this.objectHash !== this.getObjectHash();
  }

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  }

  handleCloseDatasource() {
    if (!this.hasUnsavedChanges()) {
      this.closeDataSource();
      return;
    }
    // Show confirmation modal
    this.setState({ showCloseModal: true });
  }

  handleCloseWithoutSave = () => {
    this.props.setShowModal?.(false);

    if (this.state.isTransitionPending) {
      // Discard old changes, transition to the new action from props
      this.applyTransition();
    } else {
      // Normal close (navigation away from the component entirely)
      this.setState({ showCloseModal: false });
      // Reset hash so that if closeDataSource triggers a prop change (via nextCallback),
      // the subsequent componentDidUpdate won't think there are still unsaved changes.
      this.objectHash = this.getObjectHash();
      this.closeDataSource();
    }
  };

  handleSaveAndClose = async () => {
    this.props.setShowModal?.(false);

    if (this.state.isTransitionPending) {
      // Save the OLD action using the stashed state data and effectiveActionToEdit.
      // Pass skipClose=true so the editor stays open for the next action.
      await this.handleSaveWorkflow(
        this.state.currentNodes,
        this.state.currentEdges,
        this.state.currentWorkflowName,
        EAiActionStatus.Published,
        true // skipClose — applyTransition handles loading the next action
      );
      // Then transition to the new action from props
      this.applyTransition();
    } else {
      // Normal save-and-close (navigation away from the component entirely)
      await this.handleSaveWorkflow(this.state.currentNodes, this.state.currentEdges, this.state.currentWorkflowName);
      this.setState({ showCloseModal: false });
      // Reset hash so that if closeDataSource triggers a prop change (via nextCallback),
      // the subsequent componentDidUpdate won't think there are still unsaved changes.
      this.objectHash = this.getObjectHash();
      this.closeDataSource();
    }
  };

  handleCancelCloseModal = () => {
    this.props.setShowModal?.(false);

    if (this.state.isTransitionPending) {
      // User wants to stay with the current (old) action.
      // Clear the pending flag — the editor keeps its current state.
      // effectiveActionToEdit is still the old action, so the key stays the same
      // and the WorkflowBuilder stays mounted with the old data.
      this.setState({ showCloseModal: false, isTransitionPending: false });
    } else {
      this.setState({ showCloseModal: false });
      this.props.setNextCallbackFunction?.();
    }
  };

  // Track workflow changes from WorkflowBuilder
  handleWorkflowChange = (nodes: any[], edges: any[], workflowName: string) => {
    this.setState({
      currentNodes: nodes,
      currentEdges: edges,
      currentWorkflowName: workflowName
    });
  };

  loadTemplates = async () => {
    try {
      this.setState({ isLoadingTemplates: true });
      const response = await DatasourceService.GetVisionTemplates();
      if (response?.data) {
        // Parse visionModel JSON string using parseVisionTemplate which handles node type conversion
        const templates: IVisionActionTemplate[] = response.data.map((template: IVisionTemplate) => {
          const workflowData = parseVisionTemplate(template);
          return {
            ...template,
            workflowData,
            type: workflowData.type // Extract type for easier filtering
          };
        });
        this.setState({ templates, isLoadingTemplates: false });
      }
    } catch (error) {
      console.error("Failed to load vision templates:", error);
      this.setState({ isLoadingTemplates: false });
    }
  };

  getVisionActions = (): IActionDefinitionDto[] => {
    return (this.props.sd?.actionDefinitions || []).filter((a) => a.actionType === EDatasourceType.VisionAction);
  };

  hasCreatedVisionActionBefore = (): boolean => {
    return this.getVisionActions().length > 0;
  };

  // Mirrors generateDefaultActionName / generateDefaultAgentName — scopes the uniqueness
  // check to existing Vision Actions only, so the sequence is Vision_Action1, Vision_Action2, ...
  // independent of other action types. Exposed as a static so the constructor can call it
  // before instance arrow-function fields are assigned.
  static computeDefaultVisionActionName = (actionDefinitions?: IActionDefinitionDto[]): string => {
    const baseName = "Vision_Action";
    const existingNames = new Set(
      (actionDefinitions || [])
        .filter((a) => a.actionType === EDatasourceType.VisionAction)
        .map((a) => a.name?.toLocaleLowerCase())
    );
    let counter = 1;
    while (existingNames.has(`${baseName}${counter}`.toLocaleLowerCase())) {
      counter++;
    }
    return `${baseName}${counter}`;
  };

  generateDefaultVisionActionName = (): string =>
    CreateVisionAction.computeDefaultVisionActionName(this.props.sd?.actionDefinitions);

  openDeleteActionModal = (actionDefinition: IActionDefinitionDto) => {
    this.setState({
      actionToDelete: actionDefinition,
      isDeleteActionModalVisible: true,
      recentsMenuId: null,
      recentsMenuPosition: null
    });
  };

  deleteVisionAction = async () => {
    const { actionToDelete } = this.state;
    if (!actionToDelete) return;

    try {
      this.setState({ isDeleteActionModalVisible: false });
      await DatasourceService.DeleteAction(actionToDelete);
      await this.props.sd.loadActionDefinitions();
      this.props.sd.refreshActions();

      if (this.state.effectiveActionToEdit?.id === actionToDelete.id) {
        this.closeDataSource();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ actionToDelete: undefined });
    }
  };

  closeScratchEmptyState = () => {
    this.setState({ isScratchEmptyStateVisible: false });
  };

  startBuildingFromScratch = () => {
    // Go directly to the editor with a blank canvas
    this.setState({
      createFromScratch: true,
      selectedTemplate: undefined,
      showEditor: true,
      isScratchSidebarVisible: false,
      isScratchEmptyStateVisible: false,
      actionName: this.generateDefaultVisionActionName()
    });
  };

  handleCreateFromScratchClick = () => {
    if (this.hasCreatedVisionActionBefore()) {
      this.setState({
        isScratchSidebarVisible: true,
        isScratchEmptyStateVisible: false
      });
      return;
    }

    this.setState({
      isScratchEmptyStateVisible: true,
      isScratchSidebarVisible: false
    });
  };

  renderScratchSidebar = () => {
    if (!this.state.isScratchSidebarVisible) return null;

    const closeScratch = () => this.setState({ isScratchSidebarVisible: false });

    return (
      <div className="ai-template-detail-overlay" onClick={closeScratch}>
        <div className="ai-template-detail-panel" onClick={(e) => e.stopPropagation()}>
          <div className="ai-template-detail-panel__header">
            <div className="ai-template-detail-panel__header-left">
              <KuikaButton type="tertiary" size="sm" isIcon onClick={closeScratch}>
                <KuikaSvg type="common" name="x" width={18} height={18} />
              </KuikaButton>
            </div>
            <div className="ai-template-detail-panel__header-title">
              <p className="ai-template-detail-panel__title">Build from Scratch</p>
            </div>
          </div>
          <div className="ai-template-detail-panel__body">
            <div className="ai-template-detail__header">
              <div className="ai-action-scratch-sidebar__icon">
                <KuikaSvg type="datasource-icons" name="vision-action" width={48} height={48} color="var(--green-5)" />
              </div>
              <div className="ai-template-detail__header-info">
                <div className="ai-template-detail__description-wrapper">
                  <p className="ai-template-detail__description">
                    Create a fully custom vision workflow from a blank canvas and design every node, connection, and
                    output behavior for your scenario.
                  </p>
                </div>
              </div>
            </div>
            <div className="ai-template-detail__content">
              <div className="ai-action-scratch-sidebar__features">
                {this.scratchFeatures.map((feature) => (
                  <div className="ai-action-scratch-sidebar__feature" key={feature}>
                    <KuikaSvg type="common" name="check" width={16} height={16} color="var(--green-6)" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="ai-template-detail-panel__footer">
            <KuikaButton type="primary" onClick={this.startBuildingFromScratch}>
              Start Building
            </KuikaButton>
          </div>
        </div>
      </div>
    );
  };

  handleTemplateSelect = (template: IVisionActionTemplate) => {
    // Show detail modal instead of directly selecting
    this.setState({
      modalTemplate: template,
      isModalVisible: true
    });
  };

  handleTemplateCreate = (template: IVisionActionTemplate, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    void this.createFromTemplate(template);
  };

  handleTemplateViewDetails = (template: IVisionActionTemplate, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    this.handleTemplateSelect(template);
  };

  handleModalOk = () => {
    const { modalTemplate } = this.state;
    if (!modalTemplate) return;

    // Close modal immediately
    this.setState({ isModalVisible: false, modalTemplate: undefined });

    // Start async creation process
    this.createFromTemplate(modalTemplate);
  };

  private generateUniqueTemplateActionName = (baseName: string): string => {
    const normalizedBaseName = (baseName || "").trim();
    if (!normalizedBaseName) return baseName;

    const existingNames = new Set(
      (this.props.sd.actionDefinitions || [])
        .map((action) => (action.name || "").trim().toLowerCase())
        .filter((name) => !!name)
    );

    let index = 0;
    let candidate = normalizedBaseName;
    while (existingNames.has(candidate.toLowerCase())) {
      index += 1;
      candidate = `${normalizedBaseName}${index}`;
    }

    return candidate;
  };

  createFromTemplate = async (modalTemplate: IVisionActionTemplate) => {
    this.setState({ isCreatingFromTemplate: true });

    try {
      // Step 1: Ensure VisionAction datasource exists
      let visionActionDatasourceId: string | undefined =
        this.props.actionDatasource?.id ??
        this.props.sd.datasources.find((ds) => ds.datasourceType === EDatasourceType.VisionAction)?.id;

      if (!visionActionDatasourceId) {
        await DatasourceService.SaveDatasource({
          name: "VisionActions",
          datasourceType: EDatasourceType.VisionAction
        } as IDatasourceDto);

        await this.props.sd.refreshDatabases();

        const newVisionActionDatasource = this.props.sd.datasources.find(
          (ds) => ds.datasourceType === EDatasourceType.VisionAction
        );

        if (!newVisionActionDatasource) {
          throw new Error("Vision Action datasource could not be created. Please contact customer service.");
        }

        visionActionDatasourceId = newVisionActionDatasource.id;
      }

      // Step 2: Generate unique action name
      const baseName = modalTemplate.name;
      const actionName = this.generateUniqueTemplateActionName(baseName);

      // Step 3: Create the action from template
      const action = {
        name: actionName,
        kDatasource_Id: visionActionDatasourceId,
        actionType: EDatasourceType.VisionAction,
        aiActionStatus: EAiActionStatus.Published,
        visionInfo: JSON.stringify({
          nodes: modalTemplate.workflowData?.nodes || [],
          edges: modalTemplate.workflowData?.edges || [],
          type: modalTemplate.type || "custom"
        }),
        parameters: []
      } as unknown as IActionDefinitionDto;

      const response = await DatasourceService.SaveAction(action);
      if (response.status === 200) {
        this.props.sd.refreshDatabases();
        this.props.sd.refreshActions();

        const addedAction = this.props.sd.actionDefinitions?.find((x) => x.name === actionName);

        if (this.props.sd?.creatingDatasouceFolderId && addedAction?.id) {
          const folder = this.props.sd.getFolderById(
            this.props.sd?.creatingDatasouceFolderId,
            this.props.sd.project.actionFolders
          );
          if (folder && folder.parentFolderId) {
            this.props.sd.addActionToFolderInFolder(
              folder.parentFolderId,
              this.props.sd?.creatingDatasouceFolderId,
              addedAction?.id
            );
          } else {
            this.props.sd.addActionToFolder(this.props.sd?.creatingDatasouceFolderId, addedAction?.id);
          }
        }

        // Return to hub. A template card has no button to hold a ✓: the list it closes onto
        // lights the new action's row up instead of a toast.
        requestDatasourceReveal({ name: actionName, parentId: visionActionDatasourceId });
        this.props.sd.cancelCreatingOrEditingDatasource();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isCreatingFromTemplate: false });
    }
  };

  handleModalCancel = () => {
    this.setState({
      isModalVisible: false,
      modalTemplate: undefined
    });
  };

  handleCancel = () => {
    // Close the datasource and return to previous screen (data sources screen)
    this.props.sd.cancelCreatingOrEditingDatasource();
  };

  handleContinue = () => {
    // Open workflow editor with selected template or "Create from a blank"
    this.setState({
      showEditor: true,
      actionName: this.generateDefaultVisionActionName()
    });
  };

  handleSaveWorkflow = async (
    nodes: any[],
    edges: any[],
    workflowName?: string,
    status: EAiActionStatus = EAiActionStatus.Published,
    skipClose?: boolean
  ) => {
    // The commit is live again as soon as the ✓ replaces the spinner, so a second press would
    // re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    try {
      // Step 1: Ensure VisionAction datasource exists
      let visionActionDatasourceId: string | undefined =
        this.props.actionDatasource?.id ??
        this.props.sd.datasources.find((ds) => ds.datasourceType === EDatasourceType.VisionAction)?.id;

      if (!visionActionDatasourceId) {
        await DatasourceService.SaveDatasource({
          name: "VisionActions",
          datasourceType: EDatasourceType.VisionAction
        } as IDatasourceDto);

        await this.props.sd.refreshDatabases();

        const newVisionActionDatasource = this.props.sd.datasources.find(
          (ds) => ds.datasourceType === EDatasourceType.VisionAction
        );

        if (!newVisionActionDatasource)
          throw new Error("Vision Action datasource could not be created. Please contact customer service.");

        visionActionDatasourceId = newVisionActionDatasource.id;
      }

      // Step 2: Use the workflow name from editor, or fall back to state/generated name
      const actionName = workflowName || this.state.actionName || this.generateDefaultVisionActionName();

      // Step 3: Create or update the action with visionInfo
      // Use effectiveActionToEdit (not props.actionToEdit) — props may already
      // point to a different action if a transition is pending.
      const action = {
        id: this.state.effectiveActionToEdit?.id || undefined, // Include ID if editing
        name: actionName,
        kDatasource_Id: visionActionDatasourceId,
        actionType: EDatasourceType.VisionAction,
        aiActionStatus: status,
        visionInfo: JSON.stringify({
          nodes,
          edges,
          type: this.state.selectedTemplate?.type || "custom"
        }),
        parameters: []
      } as unknown as IActionDefinitionDto;

      const response = await DatasourceService.SaveAction(action);
      if (response.status !== 200) {
        this.saveStatus.fail();
        return;
      }
      this.props.sd.refreshDatabases();
      this.props.sd.refreshActions();
      // The ✓ goes up first and the editor leaves after it — see `SaveStatus`. Not when
      // saving as part of a transition: applyTransition handles the next step.
      this.saveStatus.succeed((): void => {
        if (!skipClose) {
          // The list behind the editor lights the row up; the button has already said "Saved".
          requestDatasourceReveal({ name: actionName, parentId: visionActionDatasourceId });
          this.props.sd.cancelCreatingOrEditingDatasource();
        }
      });
    } catch (e) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(e);
    }
  };

  renderTabPane = (tab: string): React.ReactNode =>
    tab === "templates" ? this.renderTemplatesTab() : this.renderVisionRecentsTable();

  getFilteredTemplates = (): IVisionActionTemplate[] => {
    let filtered = [...this.state.templates];

    // Apply search filter
    // Normalize by removing spaces to match CamelCase names (e.g., "Phase 0" matches "Phase0")
    if (this.state.templateSearchTerm) {
      const searchNormalized = this.state.templateSearchTerm.toLowerCase().replace(/\s+/g, "");
      const searchLower = this.state.templateSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().replace(/\s+/g, "").includes(searchNormalized) ||
          t.description.toLowerCase().includes(searchLower) ||
          (t.type && t.type.toLowerCase().includes(searchLower))
      );
    }

    // Apply type filter
    if (this.state.selectedTypes.length > 0) {
      filtered = filtered.filter((t) => t.type && this.state.selectedTypes.includes(t.type));
    }

    // Apply tags filter
    if (this.state.selectedTags.length > 0) {
      filtered = filtered.filter((t) => {
        const tags = t.tags || [];
        return this.state.selectedTags.some((tag) => tags.includes(tag));
      });
    }

    // Apply sorting (by name for now - popularity/rating not in backend yet)
    switch (this.state.templateSortBy) {
      case "recent":
      case "popular":
      case "topRated":
        // Sort alphabetically by name until backend provides these metrics
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return filtered;
  };

  renderVisionRecentsTable = () => {
    const visionActions: IActionDefinitionDto[] = (this.props.sd?.actionDefinitions || []).filter(
      (a: IActionDefinitionDto) => a.actionType === EDatasourceType.VisionAction
    );

    const columns: KuikaListTableColumn<IActionDefinitionDto>[] = [
      {
        id: "name",
        header: () => "Name",
        accessorFn: (row: IActionDefinitionDto) => row.name,
        cell: (row: IActionDefinitionDto) => (
          <span
            style={
              {
                "--kuika-list-table-icon-bg": "var(--green-1)",
                "--kuika-list-table-icon-color": "var(--green-5)"
              } as React.CSSProperties
            }
          >
            <KuikaListTableNameCell
              name={row.name}
              icon={<KuikaSvg type="datasource-icons" name="vision-action" width={12} height={12} />}
            />
          </span>
        )
      },
      {
        id: "status",
        header: () => "Status",
        width: 120,
        accessorFn: (row: IActionDefinitionDto) => row.aiActionStatus ?? EAiActionStatus.Published,
        cell: (row: IActionDefinitionDto) => {
          const isDraft: boolean = (row.aiActionStatus ?? EAiActionStatus.Published) === EAiActionStatus.Draft;
          return (
            <KuikaListTableStatusCell label={isDraft ? "Draft" : "Published"} color={isDraft ? "yellow" : "green"} />
          );
        }
      },
      {
        id: "created",
        header: () => "Created",
        width: 220,
        // Sorted by WHEN: the cell's caption is the timestamp.
        accessorFn: (row: IActionDefinitionDto) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
        cell: (row: IActionDefinitionDto) => {
          const firstName: string = row.createdUser?.firstName ?? "";
          const lastName: string = row.createdUser?.lastName ?? "";
          const fullName: string = [firstName, lastName].filter(Boolean).join(" ");
          const initials: string = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
          return (
            <KuikaListTableUserCell
              caption={row.createdAt ? KMainFunctions.formatRelativeTime(row.createdAt) : undefined}
              name={fullName || undefined}
              photoUrl={row.createdUser?.profilePhotoUrl || undefined}
              initials={initials || undefined}
            />
          );
        }
      }
    ];

    return (
      <div className="ai-action-templates-tab ai-action-templates-tab--without-filters">
        <KuikaListTable<IActionDefinitionDto>
          data={visionActions}
          columns={columns}
          rowKey={(row: IActionDefinitionDto) => row.id}
          emptyTitle="No Vision Actions yet"
          emptyDescription="Start from a template or create one from a blank."
        />
      </div>
    );
  };

  renderCreateFromScratch = () => {
    const isSelected: boolean = this.state.createFromScratch;
    return (
      <KuikaCreateCardRow>
        <KuikaCreateCard
          className={isSelected ? "kuika-create-card--selected" : undefined}
          title="Create from a blank"
          description="Start with a blank canvas and build your workflow step by step."
          icon={<KuikaSvg type="datasource-icons" name="vision-action" width={24} height={24} />}
          iconBackground="var(--green-1)"
          iconColor="var(--green-5)"
          onClick={this.handleCreateFromScratchClick}
        />
      </KuikaCreateCardRow>
    );
  };

  renderTemplateCard = (template: IVisionActionTemplate) => (
    <KuikaTemplateCard
      key={template.id}
      title={KMainFunctions.formatCamelCase(template.name)}
      description={template.shortDescription || template.description}
      imageUrl={template.imageUrl}
      selected={this.state.selectedTemplate?.id === template.id}
      onClick={() => this.handleTemplateSelect(template)}
      primaryAction={{
        label: "Use template",
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => this.handleTemplateCreate(template, e),
        disabled: this.state.isCreatingFromTemplate
      }}
      secondaryAction={{
        label: "Details",
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => this.handleTemplateViewDetails(template, e)
      }}
    />
  );

  getAllTemplateTypes = (): string[] => {
    const typesSet = new Set<string>();
    this.state.templates.forEach((template) => {
      if (template.type) {
        typesSet.add(template.type);
      }
    });
    return Array.from(typesSet).sort();
  };

  getAllUniqueTags = (): string[] => {
    const tagsSet = new Set<string>();
    this.state.templates.forEach((template) => {
      const templateTags = template.tags || [];
      templateTags.forEach((tag: string) => tagsSet.add(tag));
    });
    return Array.from(tagsSet).sort();
  };

  renderTemplatesTab = () => {
    const templates = this.getFilteredTemplates();

    return (
      <div className="ai-action-templates-tab ai-action-templates-tab--without-filters">
        {templates.length === 0 && !this.state.isLoadingTemplates && !this.state.isCreatingFromTemplate ? (
          <div className="ai-action-templates-empty">
            <div className="ai-action-templates-empty__icon-container">
              <KuikaSvg type="common" name="search" width={40} height={40} color="var(--dark-3)" />
            </div>
            <h4>No templates found</h4>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <KuikaTemplateCardGrid>
            {/* Shimmering cards in the real cards' proportions rather than a dimmed
                empty grid, so the swap to content does not jump. */}
            {this.state.isLoadingTemplates || this.state.isCreatingFromTemplate ? (
              <KuikaTemplateCardSkeleton />
            ) : (
              templates.map((template) => this.renderTemplateCard(template))
            )}
          </KuikaTemplateCardGrid>
        )}
      </div>
    );
  };

  renderSelectorScreen = () => {
    const allTypes = this.getAllTemplateTypes();
    const allTags = this.getAllUniqueTags();
    const hasFilters =
      this.state.selectedTypes.length > 0 || this.state.selectedTags.length > 0 || !!this.state.templateSearchTerm;

    return (
      <div className="kuika__data-diagram__create-con vision" style={{ padding: 0 }}>
        <div className="kuika__data-diagram__create-con__container-ai-templates" style={{ height: "100%" }}>
          <div className="ai-action-selector">
            <KuikaListPageHeader
              onBack={this.handleCancel}
              backLabel="Back to AI Functions"
              icon={
                <KuikaSvg color="var(--green-5)" type="datasource-icons" name="vision-action" width={20} height={20} />
              }
              title="Add Vision Action"
            />

            {this.state.isScratchEmptyStateVisible ? (
              <CreateScratchEmptyState
                theme="green"
                iconName="vision-action"
                title="Create a Vision Action"
                description="Create a fully custom vision workflow from a blank canvas and design every node, connection, and output behavior for your scenario."
                learnMoreLink="https://academy.kuika.com/en/content/vision-actions"
                features={this.scratchFeatures}
                onBack={this.closeScratchEmptyState}
                onStart={this.startBuildingFromScratch}
              />
            ) : (
              <>
                {this.renderCreateFromScratch()}

                <KuikaListPageToolbar
                  tabs={
                    <KuikaTabs
                      size="lg"
                      options={[
                        { value: "templates", label: "Templates" },
                        { value: "recents", label: "Recents" }
                      ]}
                      value={this.state.activeTab}
                      onChange={(value) =>
                        this.setState({
                          activeTab: value as "templates" | "recents"
                        })
                      }
                    />
                  }
                  search={
                    <KuikaInput
                      size="md"
                      placeholder="Search..."
                      value={this.state.templateSearchTerm}
                      leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                      onChange={(e, value) => this.setState({ templateSearchTerm: value as string })}
                    />
                  }
                  filters={
                    this.state.activeTab === "templates" ? (
                      <>
                        {hasFilters && (
                          <KuikaTooltip content="Clear all filters" position="top">
                            {/* The tooltip refs its trigger and KuikaButton has no
                                forwardRef, so it anchors to a real element. */}
                            <span className="ai-action-clear-filters">
                              <KuikaButton
                                type="tertiary"
                                size="sm"
                                isIcon
                                onClick={() =>
                                  this.setState({
                                    selectedTypes: [],
                                    selectedTags: [],
                                    templateSearchTerm: ""
                                  })
                                }
                              >
                                <KuikaSvg name="circle-x" width={16} height={16} />
                              </KuikaButton>
                            </span>
                          </KuikaTooltip>
                        )}
                        <KuikaSelectbox
                          className={`ai-action-type-filter${
                            this.state.selectedTypes.length > 0 ? " ai-action-filter--active" : ""
                          }`}
                          size="md"
                          mode="multiple"
                          placeholder="All types"
                          value={this.state.selectedTypes}
                          onChange={(selectedTypes) =>
                            this.setState({
                              selectedTypes: selectedTypes as string[]
                            })
                          }
                          options={allTypes.map((type) => ({
                            label: type,
                            value: type
                          }))}
                          prefix={<KuikaSvg type="common" name="layout-rows" width={12} height={12} />}
                          showCountOnly
                          showOptionCheckboxes
                          dropdownMatchSelectWidth={false}
                        />
                        <KuikaSelectbox
                          className={`ai-action-tag-filter${
                            this.state.selectedTags.length > 0 ? " ai-action-filter--active" : ""
                          }`}
                          size="md"
                          mode="multiple"
                          placeholder="All tags"
                          value={this.state.selectedTags}
                          onChange={(selectedTags) =>
                            this.setState({
                              selectedTags: selectedTags as string[]
                            })
                          }
                          options={allTags.map((tag) => ({
                            label: tag,
                            value: tag
                          }))}
                          prefix={<KuikaSvg name="tag" type="common" width={12} height={12} />}
                          showCountOnly
                          dropdownMatchSelectWidth={false}
                          showOptionCheckboxes
                          countLabel="tags applied"
                        />
                      </>
                    ) : undefined
                  }
                />

                {/* Templates and Recents are two views of the same list page: the new one slides in
                    from the side the selection moved towards instead of cutting. */}
                <KuikaTabContent
                  className="vision-action__tab-content"
                  activeKey={this.state.activeTab}
                  order={VISION_TAB_ORDER}
                  render={this.renderTabPane}
                />
              </>
            )}
            {this.renderScratchSidebar()}
          </div>
          {/* <div className="ai-action__footer">
            <KuikaButton type="text" onClick={this.handleCancel}>
              Cancel
            </KuikaButton>
            <KuikaButton
              type="primary"
              onClick={this.handleContinue}
              disabled={!this.state.selectedTemplate && !this.state.createFromScratch}
            >
              NEXT
            </KuikaButton>
          </div> */}
        </div>
      </div>
    );
  };

  renderEditor = () => {
    // Use effectiveActionToEdit (state) — NOT this.props.actionToEdit.
    // During a pending transition the props already point to the NEW action,
    // but we must keep rendering the OLD action until the user confirms.
    const action = this.state.effectiveActionToEdit;

    let initialNodes: any[] = [];
    let initialEdges: any[] = [];
    let initialName = this.state.actionName;

    if (action?.visionInfo) {
      try {
        const parsed = parseVisionInfo(action.visionInfo);
        initialNodes = parsed.nodes || [];
        initialEdges = parsed.edges || [];
      } catch (e) {
        console.error("Failed to parse visionInfo:", e);
      }
    } else if (this.state.selectedTemplate?.workflowData) {
      initialNodes = this.state.selectedTemplate.workflowData.nodes || [];
      initialEdges = this.state.selectedTemplate.workflowData.edges || [];
    }

    // Key is derived from state, not props.
    // This prevents the WorkflowBuilder from remounting prematurely
    // when props change but the user hasn't confirmed the transition yet.
    const workflowKey = action?.id || `new-${this.state.selectedTemplate?.id || "scratch"}`;

    return (
      <ReactFlowProvider>
        <DnDProvider>
          <WorkflowProvider>
            <WorkflowBuilder
              key={workflowKey}
              onSave={this.handleSaveWorkflow}
              saveStatus={this.state.saveStatus}
              onCancel={() => this.handleCloseDatasource()}
              onChange={this.handleWorkflowChange}
              initialNodes={initialNodes}
              initialEdges={initialEdges}
              initialName={initialName}
              isEditing={!!action}
              switcher={
                <AiActionSwitcherChip
                  sd={this.props.sd}
                  setNextCallbackFunction={(callback?: () => void): void =>
                    this.props.setNextCallbackFunction?.(callback)
                  }
                  actionType={EDatasourceType.VisionAction}
                  currentActionId={action?.id}
                  currentName={initialName}
                  triggerTestId="visionActionSwitcherTrigger"
                  onManageAll={() => this.handleCloseDatasource()}
                />
              }
            />
          </WorkflowProvider>
        </DnDProvider>
      </ReactFlowProvider>
    );
  };

  render() {
    return (
      <>
        <DeleteModal
          itemName={this.state.actionToDelete?.name || ""}
          triggerModal={this.state.isDeleteActionModalVisible}
          deleteItem={this.deleteVisionAction}
          deleteType={"action"}
          closeModal={() =>
            this.setState({
              actionToDelete: undefined,
              isDeleteActionModalVisible: false
            })
          }
        />
        {this.state.showEditor ? this.renderEditor() : this.renderSelectorScreen()}

        <VisionTemplateDetailModal
          visible={this.state.isModalVisible}
          template={this.state.modalTemplate || null}
          onOk={this.handleModalOk}
          onCancel={this.handleModalCancel}
        />

        <CloseConfirmationModal
          itemName={this.state.currentWorkflowName || this.state.actionName || "Vision Action"}
          closeType="Vision Action"
          triggerModal={this.state.showCloseModal}
          closeWithoutSaveHandler={this.handleCloseWithoutSave}
          updateAndCloseHandler={this.handleSaveAndClose}
          cancel={this.handleCancelCloseModal}
        />
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch: any) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createVisionAction = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ICreateVisionActionProps>(CreateVisionAction));
export { createVisionAction as CreateVisionAction };
